import React, { useCallback, useEffect, useMemo, useState } from "react";

import BreadCrumb from "@/Components/Common/BreadCrumb";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "@/Components/ui";
import {
  FrontTerminal,
  TableOrderPayment,
  posService,
} from "@/api/services/pos.service";
import { dialogService } from "@/utils/dialogService";
import {
  CustomerPhoneCaptureDetail,
  CustomerPhoneCaptureMode,
  CustomerRegistrationState,
  Menu,
  MenuOption,
  MenuOptionGroup,
  ServiceTable,
  TableGroup,
  TableOrder,
  tableOrderService,
} from "@/api/services/table-order.service";
import { toast } from "react-hot-toast";

import PostCancelDecisionModal from "./PostCancelDecisionModal";
import PreviousOrderLookupModal from "./PreviousOrderLookupModal";
import { getErrorToastMessage } from "./getErrorToastMessage";
import TerminalSettingsPanel from "../Terminals/TerminalSettingsPanel";

type CartOption = {
  menu_option_id: number;
  option_name_snapshot: string;
  extra_price_snapshot: number;
  quantity: number;
};

type CartItem = {
  id: string;
  menu_id: number;
  menu_name_snapshot: string;
  fulfillment_type: Menu["fulfillment_type"];
  quantity: number;
  unit_price_snapshot: number;
  line_amount: number;
  options: CartOption[];
};

type PageTab = "compose" | "checkout";
type OperatorPanelTab = "payment" | "history" | "previous" | "webPending";

const orderTypeLabels: Record<TableOrder["order_type"], string> = {
  serving: "서빙",
  pickup: "픽업",
};

const paymentStatusLabels: Record<TableOrder["payment_status"], string> = {
  UNPAID: "미결제",
  PAYMENT_PENDING: "결제요청중",
  PAID: "결제완료",
  PAYMENT_FAILED: "결제실패",
  CANCEL_PENDING: "취소요청중",
  CANCELED: "취소완료",
};

const requestTypeLabels: Record<TableOrderPayment["request_type"], string> = {
  PAY: "결제",
  CANCEL: "취소",
  REPRINT: "재출력",
};

const paymentRequestStatusLabels: Record<TableOrderPayment["status"], string> =
  {
    PENDING: "대기",
    PROCESSING: "진행중",
    SUCCEEDED: "성공",
    FAILED: "실패",
  };

const customerCaptureModeLabels: Record<CustomerPhoneCaptureMode, string> = {
  CRM_DIRECT: "직접 입력",
  TOSS_FRONT: "단말 입력",
  SKIP: "정보 없음",
};

const customerCaptureDetailLabels: Record<CustomerPhoneCaptureDetail, string> =
  {
    NONE: "없음",
    MANUAL: "직접 입력",
    KEYPAD: "단말 키패드",
    QR: "회원 QR",
  };

const customerRegistrationLabels: Record<CustomerRegistrationState, string> = {
  NONE: "미연결",
  EXISTING_USER: "기존회원 연결",
  AUTO_CREATED_USER: "회원 자동생성",
};

const orderStatusLabels: Record<TableOrder["status"], string> = {
  NEW: "신규",
  ACCEPTED: "접수",
  SERVED: "서빙완료",
  CANCELED: "주문취소",
};

const fulfillmentTypeLabels: Record<NonNullable<Menu["fulfillment_type"]>, string> = {
  INSTANT: "즉시 제공",
  KITCHEN: "제조 필요",
};

const pageTabLabels: Record<PageTab, string> = {
  compose: "주문 작성",
  checkout: "주문 확인 및 결제 진행",
};

const operatorPanelTabLabels: Record<OperatorPanelTab, string> = {
  payment: "결제 요청",
  history: "결제 이력",
  previous: "이전 주문 불러오기",
  webPending: "웹 주문 결제대기",
};

const orderedCustomerCaptureModes: CustomerPhoneCaptureMode[] = [
  "TOSS_FRONT",
  "CRM_DIRECT",
  "SKIP",
];

const formatCurrency = (value: number) => `${value.toLocaleString()}원`;

const getTerminalLabel = (terminal: FrontTerminal) =>
  terminal.label || terminal.serial_number;

const formatDateTime = (value: string | null) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString();
};

const formatDateInputValue = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const sortByDisplayOrder = <T extends { id: number; display_order: number }>(
  items: T[],
) => {
  return [...items].sort(
    (left, right) =>
      left.display_order - right.display_order || left.id - right.id,
  );
};

const getMenuOptionGroups = (menu: Menu) => {
  return sortByDisplayOrder(
    (menu.option_groups || []).filter((group) => group.is_active),
  ).map((group) => ({
    ...group,
    options: sortByDisplayOrder(
      (group.options || []).filter((option) => option.is_active),
    ),
  }));
};

const getLineOptionTotal = (item: Pick<CartItem, "options">) => {
  return (item.options || []).reduce(
    (sum, option) => sum + option.extra_price_snapshot * (option.quantity || 1),
    0,
  );
};

const buildCartItem = (
  menu: Menu,
  quantity: number,
  selectedOptions: CartOption[],
): CartItem => {
  const optionTotal = selectedOptions.reduce(
    (sum, option) => sum + option.extra_price_snapshot * (option.quantity || 1),
    0,
  );

  return {
    id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    menu_id: menu.id,
    menu_name_snapshot: menu.name,
    fulfillment_type: menu.fulfillment_type,
    quantity,
    unit_price_snapshot: menu.price,
    line_amount: (menu.price + optionTotal) * quantity,
    options: selectedOptions,
  };
};

const buildCartItemsFromTableOrder = (tableOrder: TableOrder): CartItem[] => {
  return (tableOrder.items || []).map((item) => ({
    id: `table-order-item-${item.id}`,
    menu_id: item.menu_id,
    menu_name_snapshot: item.menu_name_snapshot,
    fulfillment_type: tableOrder.fulfillment_type || "KITCHEN",
    quantity: item.quantity,
    unit_price_snapshot: item.unit_price_snapshot,
    line_amount: item.line_amount,
    options: (item.options || []).map((option) => ({
      menu_option_id: option.menu_option_id || 0,
      option_name_snapshot: option.option_name_snapshot,
      extra_price_snapshot: option.extra_price_snapshot,
      quantity: option.quantity,
    })),
  }));
};

const parseOptionalNumber = (value: string) => {
  if (!value.trim()) {
    return undefined;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const normalizePhoneInput = (value: string) => value.replace(/[^0-9]/g, "");

const getPreferredTerminalCaptureDetail = ({
  terminalCustomerPhoneInputAvailable,
  terminalCustomerQrAvailable,
}: {
  terminalCustomerPhoneInputAvailable: boolean;
  terminalCustomerQrAvailable: boolean;
}): CustomerPhoneCaptureDetail => {
  if (terminalCustomerPhoneInputAvailable) {
    return "KEYPAD";
  }
  if (terminalCustomerQrAvailable) {
    return "QR";
  }
  return "NONE";
};

const statusPillClassName = (status: string) => {
  if (status === "PAID" || status === "SUCCEEDED" || status === "온라인") {
    return "bg-emerald-100 text-emerald-700";
  }
  if (
    status === "PAYMENT_PENDING" ||
    status === "CANCEL_PENDING" ||
    status === "PENDING" ||
    status === "PROCESSING" ||
    status === "지연"
  ) {
    return "bg-amber-100 text-amber-700";
  }
  if (
    status === "PAYMENT_FAILED" ||
    status === "FAILED" ||
    status === "오프라인"
  ) {
    return "bg-red-100 text-red-700";
  }
  if (status === "CANCELED") {
    return "bg-slate-200 text-slate-700";
  }
  return "bg-sky-100 text-sky-700";
};

const getSubmitOrderBlockReason = ({
  cartItemsLength,
  customerCaptureMode,
  orderType,
  normalizedCustomerPhone,
  selectedTableId,
  submittedOrder,
  terminalCustomerCaptureAvailable,
}: {
  cartItemsLength: number;
  customerCaptureMode: CustomerPhoneCaptureMode;
  orderType: TableOrder["order_type"];
  normalizedCustomerPhone: string;
  selectedTableId: string;
  submittedOrder: TableOrder | null;
  terminalCustomerCaptureAvailable: boolean;
}) => {
  if (submittedOrder) {
    return `이미 주문 #${submittedOrder.id} 이 생성되었습니다. 새 주문을 만들려면 '새 주문 시작'을 눌러주세요.`;
  }

  if (cartItemsLength === 0) {
    return "메뉴를 먼저 담아주세요.";
  }

  if (orderType === "serving" && !selectedTableId) {
    return "서빙 주문은 테이블을 선택해야 합니다.";
  }

  if (customerCaptureMode === "CRM_DIRECT" && !normalizedCustomerPhone) {
    return "직접 입력 모드에서는 고객 전화번호를 입력해야 합니다.";
  }

  if (
    customerCaptureMode === "CRM_DIRECT" &&
    (normalizedCustomerPhone.length < 8 || normalizedCustomerPhone.length > 15)
  ) {
    return "고객 전화번호는 8자리 이상 15자리 이하 숫자로 입력해주세요.";
  }

  if (
    customerCaptureMode === "TOSS_FRONT" &&
    !terminalCustomerCaptureAvailable
  ) {
    return "현재 연결된 단말에서는 단말 고객 캡처를 지원하지 않습니다.";
  }

  return "";
};

const getSubmitOrderButtonLabel = ({
  submittingOrder,
  submitOrderBlockReason,
  submittedOrder,
}: {
  submittingOrder: boolean;
  submitOrderBlockReason: string;
  submittedOrder: TableOrder | null;
}) => {
  if (submittingOrder) {
    return "주문 생성 중...";
  }
  if (submittedOrder) {
    return `주문 #${submittedOrder.id} 생성 완료`;
  }
  if (!submitOrderBlockReason) {
    return "주문 생성";
  }
  if (submitOrderBlockReason.includes("테이블")) {
    return "테이블 선택 필요";
  }
  if (submitOrderBlockReason.includes("전화번호")) {
    return "전화번호 확인 필요";
  }
  if (submitOrderBlockReason.includes("단말")) {
    return "수집 방식 확인 필요";
  }
  return "주문 생성 불가";
};

const CashierPOS = () => {
  const [loading, setLoading] = useState(false);
  const [submittingOrder, setSubmittingOrder] = useState(false);
  const [creatingPayment, setCreatingPayment] = useState(false);
  const [actingPaymentId, setActingPaymentId] = useState<number | null>(null);
  const [isTerminalSettingsModalOpen, setIsTerminalSettingsModalOpen] =
    useState(false);
  const [isPostCancelDecisionOpen, setIsPostCancelDecisionOpen] =
    useState(false);
  const [postCancelActionPending, setPostCancelActionPending] = useState<
    "REOPEN_PAYMENT" | "CANCEL_ORDER" | null
  >(null);
  const [activePageTab, setActivePageTab] = useState<PageTab>("compose");
  const [activeOperatorTab, setActiveOperatorTab] =
    useState<OperatorPanelTab>("payment");
  const [hasTouchedCustomerCaptureMode, setHasTouchedCustomerCaptureMode] =
    useState(false);

  const [menus, setMenus] = useState<Menu[]>([]);
  const [categories, setCategories] = useState<
    Array<{ id: number; name: string; display_order: number }>
  >([]);
  const [tableGroups, setTableGroups] = useState<TableGroup[]>([]);
  const [tables, setTables] = useState<ServiceTable[]>([]);
  const [terminals, setTerminals] = useState<FrontTerminal[]>([]);

  const [selectedCategoryId, setSelectedCategoryId] = useState<string>("all");
  const [orderType, setOrderType] =
    useState<TableOrder["order_type"]>("serving");
  const [selectedTableGroupId, setSelectedTableGroupId] =
    useState<string>("all");
  const [selectedTableId, setSelectedTableId] = useState<string>("");
  const [memo, setMemo] = useState("");
  const [customerCaptureMode, setCustomerCaptureMode] =
    useState<CustomerPhoneCaptureMode>("SKIP");
  const [customerCaptureDetail, setCustomerCaptureDetail] =
    useState<CustomerPhoneCaptureDetail>("NONE");
  const [customerPhoneInput, setCustomerPhoneInput] = useState("");
  const [customerNameInput, setCustomerNameInput] = useState("");

  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [menuPicker, setMenuPicker] = useState<Menu | null>(null);
  const [menuPickerQuantity, setMenuPickerQuantity] = useState(1);
  const [menuPickerOptions, setMenuPickerOptions] = useState<
    Record<number, number[]>
  >({});

  const [submittedOrder, setSubmittedOrder] = useState<TableOrder | null>(null);
  const [payments, setPayments] = useState<TableOrderPayment[]>([]);
  const [webPendingOrders, setWebPendingOrders] = useState<TableOrder[]>([]);
  const [webPendingLoading, setWebPendingLoading] = useState(false);
  const [webPendingRefreshing, setWebPendingRefreshing] = useState(false);
  const [selectedTerminalId, setSelectedTerminalId] = useState<string>("");
  const [paymentForm, setPaymentForm] = useState({
    amount: "",
    taxAmount: "",
    supplyAmount: "",
    taxFreeAmount: "",
    tipAmount: "",
  });

  const loadCatalog = useCallback(async () => {
    setLoading(true);
    try {
      const [
        categoryResponse,
        menuResponse,
        tableGroupResponse,
        tableResponse,
        terminalResponse,
      ] = await Promise.all([
        tableOrderService.listAllMenuCategories({
          is_active: true,
          visibility_target: "pos",
        }),
        tableOrderService.listAllMenus({
          is_active: true,
          visibility_target: "pos",
        }),
        tableOrderService.listAllTableGroups({ is_active: true }),
        tableOrderService.listAllServiceTables({ is_active: true }),
        posService.listFrontTerminals(),
      ]);

      const nextMenus = sortByDisplayOrder(
        (menuResponse.results || []).filter(
          (menu) => menu.is_active && menu.is_pos_visible,
        ),
      );
      const visibleCategoryIds = new Set(nextMenus.map((menu) => menu.category));

      const nextCategories = sortByDisplayOrder(
        (categoryResponse.results || []).filter(
          (category) =>
            category.is_active &&
            category.is_pos_visible &&
            visibleCategoryIds.has(category.id),
        ),
      );
      const nextTableGroups = sortByDisplayOrder(
        (tableGroupResponse.results || []).filter((group) => group.is_active),
      );
      const nextTables = sortByDisplayOrder(
        (tableResponse.results || []).filter((table) => table.is_active),
      );
      const nextTerminals = [...(terminalResponse.data || [])].sort(
        (left, right) => {
          return (
            left.label.localeCompare(right.label || left.serial_number) ||
            left.id - right.id
          );
        },
      );

      setCategories(nextCategories);
      setMenus(nextMenus);
      setTableGroups(nextTableGroups);
      setTables(nextTables);
      setTerminals(nextTerminals);
      setSelectedCategoryId((current) => {
        if (
          current !== "all" &&
          nextCategories.some((category) => String(category.id) === current)
        ) {
          return current;
        }
        return nextCategories[0] ? String(nextCategories[0].id) : "all";
      });

      const firstActiveTerminal = nextTerminals.find(
        (terminal) => terminal.status === "ACTIVE",
      );
      setSelectedTerminalId((current) => {
        if (
          current &&
          nextTerminals.some(
            (terminal) =>
              String(terminal.id) === current && terminal.status === "ACTIVE",
          )
        ) {
          return current;
        }
        return firstActiveTerminal ? String(firstActiveTerminal.id) : "";
      });
    } catch (error) {
      toast.error(
        getErrorToastMessage(error, "POS 기본 데이터를 불러오지 못했습니다."),
      );
    } finally {
      setLoading(false);
    }
  }, []);

  const loadOrderAndPayments = useCallback(
    async (tableOrderId: number, silent = false) => {
      try {
        const [orderResponse, paymentResponse] = await Promise.all([
          tableOrderService.getTableOrder(tableOrderId),
          posService.listTableOrderPayments(tableOrderId),
        ]);
        const nextPayments = [...(paymentResponse.data || [])].sort(
          (left, right) => {
            return (
              new Date(right.requested_at).getTime() -
              new Date(left.requested_at).getTime()
            );
          },
        );
        setSubmittedOrder(orderResponse.data);
        setPayments(nextPayments);
      } catch (error) {
        if (!silent) {
          toast.error(
            getErrorToastMessage(
              error,
              "주문 또는 결제 상태를 새로고침하지 못했습니다.",
            ),
          );
        }
      }
    },
    [],
  );

  const loadWebPendingOrders = useCallback(async (background = false) => {
    const today = formatDateInputValue(new Date());

    if (background) {
      setWebPendingRefreshing(true);
    } else {
      setWebPendingLoading(true);
    }

    try {
      const response = await tableOrderService.listTableOrders({
        order_source: "WEB_CUSTOMER",
        status: "NEW,ACCEPTED",
        payment_status: "UNPAID,PAYMENT_FAILED",
        date_from: today,
        date_to: today,
        page_size: 50,
      });
      setWebPendingOrders(response.data.results || []);
    } catch (error) {
      if (!background) {
        toast.error(
          getErrorToastMessage(error, "웹 주문 결제대기 목록을 불러오지 못했습니다."),
        );
      }
    } finally {
      setWebPendingLoading(false);
      setWebPendingRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void loadCatalog();
    void loadWebPendingOrders();
  }, [loadCatalog, loadWebPendingOrders]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      void loadWebPendingOrders(true);
    }, 10000);

    return () => {
      window.clearInterval(timer);
    };
  }, [loadWebPendingOrders]);

  useEffect(() => {
    if (!submittedOrder) {
      return;
    }

    const timer = window.setInterval(() => {
      void loadOrderAndPayments(submittedOrder.id, true);
    }, 5000);

    return () => {
      window.clearInterval(timer);
    };
  }, [loadOrderAndPayments, submittedOrder]);

  useEffect(() => {
    if (!submittedOrder) {
      return;
    }

    void loadWebPendingOrders(true);
  }, [loadWebPendingOrders, submittedOrder?.id, submittedOrder?.payment_status, submittedOrder?.status]);

  useEffect(() => {
    if (!submittedOrder) {
      setPaymentForm({
        amount: "",
        taxAmount: "",
        supplyAmount: "",
        taxFreeAmount: "",
        tipAmount: "",
      });
      return;
    }

    setPaymentForm({
      amount: String(submittedOrder.total_amount),
      taxAmount: "",
      supplyAmount: "",
      taxFreeAmount: "",
      tipAmount: "",
    });
  }, [submittedOrder]);

  useEffect(() => {
    if (!submittedOrder) {
      return;
    }

    setCartItems(buildCartItemsFromTableOrder(submittedOrder));
    setOrderType(submittedOrder.order_type);
    setMemo(submittedOrder.memo || "");
    setSelectedTableId(
      submittedOrder.table ? String(submittedOrder.table) : "",
    );
    setCustomerCaptureMode(
      submittedOrder.customer_phone_capture_mode || "SKIP",
    );
    setCustomerCaptureDetail(
      submittedOrder.customer_phone_capture_detail || "NONE",
    );
    setCustomerPhoneInput(submittedOrder.customer_phone_num || "");
    setCustomerNameInput(submittedOrder.customer_name || "");

    if (submittedOrder.table) {
      const matchedTable = tables.find(
        (table) => table.id === submittedOrder.table,
      );
      setSelectedTableGroupId(
        matchedTable?.group ? String(matchedTable.group) : "all",
      );
    } else {
      setSelectedTableGroupId("all");
    }
  }, [submittedOrder, tables]);

  const activeTerminals = useMemo(
    () => terminals.filter((terminal) => terminal.status === "ACTIVE"),
    [terminals],
  );

  const terminalCustomerPhoneInputAvailable = useMemo(
    () =>
      activeTerminals.some((terminal) =>
        Boolean(terminal.capabilities?.supports_customer_phone_input),
      ),
    [activeTerminals],
  );

  const terminalCustomerQrAvailable = useMemo(
    () =>
      activeTerminals.some((terminal) =>
        Boolean(terminal.capabilities?.supports_customer_phone_qr),
      ),
    [activeTerminals],
  );

  const terminalCustomerCaptureAvailable = useMemo(
    () => terminalCustomerPhoneInputAvailable || terminalCustomerQrAvailable,
    [terminalCustomerPhoneInputAvailable, terminalCustomerQrAvailable],
  );

  const filteredMenus = useMemo(() => {
    return menus.filter((menu) => {
      if (!menu.is_pos_visible) {
        return false;
      }
      if (selectedCategoryId === "all") {
        return true;
      }
      return String(menu.category) === selectedCategoryId;
    });
  }, [menus, selectedCategoryId]);

  const filteredTables = useMemo(() => {
    return tables.filter((table) => {
      if (selectedTableGroupId === "all") {
        return true;
      }
      return String(table.group || "") === selectedTableGroupId;
    });
  }, [selectedTableGroupId, tables]);

  const cartTotal = useMemo(
    () => cartItems.reduce((sum, item) => sum + item.line_amount, 0),
    [cartItems],
  );

  const latestSuccessfulPay = useMemo(
    () =>
      payments.find(
        (payment) =>
          payment.request_type === "PAY" && payment.status === "SUCCEEDED",
      ) || null,
    [payments],
  );

  const latestReprintTarget = useMemo(
    () =>
      payments.find(
        (payment) =>
          payment.status === "SUCCEEDED" &&
          (payment.request_type === "PAY" || payment.request_type === "CANCEL"),
      ) || null,
    [payments],
  );

  const latestSuccessfulCancel = useMemo(
    () =>
      payments.find(
        (payment) =>
          payment.request_type === "CANCEL" && payment.status === "SUCCEEDED",
      ) || null,
    [payments],
  );

  const hasPendingPayRequest = useMemo(
    () =>
      payments.some(
        (payment) =>
          payment.request_type === "PAY" &&
          (payment.status === "PENDING" || payment.status === "PROCESSING"),
      ),
    [payments],
  );

  const selectedTable = useMemo(
    () => tables.find((table) => String(table.id) === selectedTableId) || null,
    [selectedTableId, tables],
  );

  const selectedTerminal = useMemo(
    () =>
      terminals.find(
        (terminal) => String(terminal.id) === selectedTerminalId,
      ) || null,
    [selectedTerminalId, terminals],
  );
  const selectedTerminalSummary = useMemo(() => {
    if (!selectedTerminal) {
      return "단말 미선택";
    }

    return `${getTerminalLabel(selectedTerminal)} / ${selectedTerminal.serial_number}`;
  }, [selectedTerminal]);

  const canCancelOrderAfterPaymentCancel = useMemo(
    () =>
      submittedOrder?.status === "NEW" || submittedOrder?.status === "ACCEPTED",
    [submittedOrder?.status],
  );

  useEffect(() => {
    if (!submittedOrder || !latestSuccessfulCancel) {
      return;
    }

    if (
      submittedOrder.payment_status === "CANCELED" &&
      submittedOrder.status !== "CANCELED"
    ) {
      setIsPostCancelDecisionOpen(true);
    }
  }, [latestSuccessfulCancel, submittedOrder]);

  useEffect(() => {
    if (customerCaptureMode !== "TOSS_FRONT" || Boolean(submittedOrder)) {
      return;
    }

    const preferredDetail = getPreferredTerminalCaptureDetail({
      terminalCustomerPhoneInputAvailable,
      terminalCustomerQrAvailable,
    });

    if (
      preferredDetail !== "NONE" &&
      customerCaptureDetail === preferredDetail
    ) {
      return;
    }

    if (preferredDetail !== "NONE") {
      setCustomerCaptureDetail(preferredDetail);
    }
  }, [
    customerCaptureDetail,
    customerCaptureMode,
    submittedOrder,
    terminalCustomerPhoneInputAvailable,
    terminalCustomerQrAvailable,
  ]);

  useEffect(() => {
    if (
      submittedOrder ||
      customerPhoneInput ||
      customerNameInput ||
      hasTouchedCustomerCaptureMode
    ) {
      return;
    }

    if (customerCaptureMode === "SKIP" && terminalCustomerCaptureAvailable) {
      setCustomerCaptureMode("TOSS_FRONT");
      setCustomerCaptureDetail(
        getPreferredTerminalCaptureDetail({
          terminalCustomerPhoneInputAvailable,
          terminalCustomerQrAvailable,
        }),
      );
    }
  }, [
    customerCaptureMode,
    customerNameInput,
    customerPhoneInput,
    hasTouchedCustomerCaptureMode,
    submittedOrder,
    terminalCustomerCaptureAvailable,
    terminalCustomerPhoneInputAvailable,
    terminalCustomerQrAvailable,
  ]);

  const handleTerminalsUpdated = useCallback(
    (nextTerminals: FrontTerminal[]) => {
      setTerminals(nextTerminals);
      setSelectedTerminalId((current) => {
        if (
          current &&
          nextTerminals.some(
            (terminal) =>
              String(terminal.id) === current && terminal.status === "ACTIVE",
          )
        ) {
          return current;
        }

        const firstActiveTerminal = nextTerminals.find(
          (terminal) => terminal.status === "ACTIVE",
        );
        return firstActiveTerminal ? String(firstActiveTerminal.id) : "";
      });
    },
    [],
  );

  const resetDraft = useCallback(() => {
    setCartItems([]);
    setMenuPicker(null);
    setMenuPickerQuantity(1);
    setMenuPickerOptions({});
    setSubmittedOrder(null);
    setPayments([]);
    setMemo("");
    setOrderType("serving");
    setSelectedTableGroupId("all");
    setSelectedTableId("");
    setCustomerCaptureMode(
      terminalCustomerCaptureAvailable ? "TOSS_FRONT" : "SKIP",
    );
    setCustomerCaptureDetail(
      terminalCustomerCaptureAvailable
        ? getPreferredTerminalCaptureDetail({
            terminalCustomerPhoneInputAvailable,
            terminalCustomerQrAvailable,
          })
        : "NONE",
    );
    setCustomerPhoneInput("");
    setCustomerNameInput("");
    setPaymentForm({
      amount: "",
      taxAmount: "",
      supplyAmount: "",
      taxFreeAmount: "",
      tipAmount: "",
    });
    setHasTouchedCustomerCaptureMode(false);
    setActivePageTab("compose");
    setActiveOperatorTab("payment");
  }, [
    terminalCustomerCaptureAvailable,
    terminalCustomerPhoneInputAvailable,
    terminalCustomerQrAvailable,
  ]);

  const appendCartItem = useCallback((item: CartItem) => {
    setCartItems((current) => {
      const existingFulfillmentType = current[0]?.fulfillment_type;
      if (
        existingFulfillmentType &&
        existingFulfillmentType !== item.fulfillment_type
      ) {
        toast.error(
          "즉시 제공 상품과 제조 필요 상품은 같은 주문에 함께 담을 수 없습니다.",
        );
        return current;
      }
      return [...current, item];
    });
  }, []);

  const openMenuPicker = useCallback(
    (menu: Menu) => {
      const optionGroups = getMenuOptionGroups(menu);
      if (optionGroups.length === 0) {
        appendCartItem(buildCartItem(menu, 1, []));
        toast.success(`${menu.name} 메뉴를 담았습니다.`);
        return;
      }

      setMenuPicker(menu);
      setMenuPickerQuantity(1);
      setMenuPickerOptions({});
    },
    [appendCartItem],
  );

  const closeMenuPicker = useCallback(() => {
    setMenuPicker(null);
    setMenuPickerQuantity(1);
    setMenuPickerOptions({});
  }, []);

  const togglePickerOption = useCallback(
    (group: MenuOptionGroup, option: MenuOption, checked: boolean) => {
      setMenuPickerOptions((current) => {
        const selectedIds = current[group.id] || [];
        let nextValues: number[] = [];

        if (group.max_select <= 1) {
          nextValues = checked ? [option.id] : [];
        } else if (checked) {
          if (selectedIds.includes(option.id)) {
            nextValues = selectedIds;
          } else {
            if (selectedIds.length >= group.max_select) {
              toast.error(
                `${group.name} 옵션은 최대 ${group.max_select}개까지 선택할 수 있습니다.`,
              );
              return current;
            }
            nextValues = [...selectedIds, option.id];
          }
        } else {
          nextValues = selectedIds.filter(
            (selectedId) => selectedId !== option.id,
          );
        }

        return {
          ...current,
          [group.id]: nextValues,
        };
      });
    },
    [],
  );

  const handleAddMenuToCart = useCallback(() => {
    if (!menuPicker) {
      return;
    }

    const selectedOptions: CartOption[] = [];
    for (const group of getMenuOptionGroups(menuPicker)) {
      const selectedIds = menuPickerOptions[group.id] || [];
      if (selectedIds.length < group.min_select) {
        toast.error(
          `${group.name} 옵션은 최소 ${group.min_select}개를 선택해야 합니다.`,
        );
        return;
      }
      if (selectedIds.length > group.max_select) {
        toast.error(
          `${group.name} 옵션은 최대 ${group.max_select}개까지만 선택할 수 있습니다.`,
        );
        return;
      }

      for (const option of group.options || []) {
        if (selectedIds.includes(option.id)) {
          selectedOptions.push({
            menu_option_id: option.id,
            option_name_snapshot: option.name,
            extra_price_snapshot: option.extra_price,
            quantity: 1,
          });
        }
      }
    }

    appendCartItem(
      buildCartItem(menuPicker, menuPickerQuantity, selectedOptions),
    );
    toast.success(`${menuPicker.name} 메뉴를 담았습니다.`);
    closeMenuPicker();
  }, [
    appendCartItem,
    closeMenuPicker,
    menuPicker,
    menuPickerOptions,
    menuPickerQuantity,
  ]);

  const updateCartItemQuantity = useCallback(
    (index: number, nextQuantity: number) => {
      if (nextQuantity < 1) {
        return;
      }

      setCartItems((current) => {
        const nextItems = [...current];
        const target = nextItems[index];
        if (!target) {
          return current;
        }

        target.quantity = nextQuantity;
        target.line_amount =
          (target.unit_price_snapshot + getLineOptionTotal(target)) *
          nextQuantity;
        return nextItems;
      });
    },
    [],
  );

  const removeCartItem = useCallback((index: number) => {
    setCartItems((current) =>
      current.filter((_, currentIndex) => currentIndex !== index),
    );
  }, []);

  const handleSubmitOrder = useCallback(async () => {
    const blockReason = getSubmitOrderBlockReason({
      cartItemsLength: cartItems.length,
      customerCaptureMode,
      orderType,
      normalizedCustomerPhone: normalizePhoneInput(customerPhoneInput),
      selectedTableId,
      submittedOrder,
      terminalCustomerCaptureAvailable,
    });
    if (blockReason) {
      toast.error(blockReason);
      return;
    }

    const normalizedCustomerPhone = normalizePhoneInput(customerPhoneInput);

    setSubmittingOrder(true);
    try {
      const response = await posService.submitTableOrder({
        order_source: "CRM_POS",
        order_type: orderType,
        table_id: orderType === "serving" ? Number(selectedTableId) : undefined,
        memo: memo.trim(),
        customer_phone_num:
          customerCaptureMode === "CRM_DIRECT" ? normalizedCustomerPhone : "",
        customer_name:
          customerCaptureMode === "CRM_DIRECT" ? customerNameInput.trim() : "",
        customer_phone_capture_mode: customerCaptureMode,
        customer_phone_capture_detail:
          customerCaptureMode === "CRM_DIRECT"
            ? "MANUAL"
            : customerCaptureMode === "TOSS_FRONT"
              ? getPreferredTerminalCaptureDetail({
                  terminalCustomerPhoneInputAvailable,
                  terminalCustomerQrAvailable,
                })
              : "NONE",
        items: cartItems.map((item) => ({
          menu_id: item.menu_id,
          quantity: item.quantity,
          options: item.options.map((option) => ({
            menu_option_id: option.menu_option_id,
            quantity: option.quantity || 1,
          })),
        })),
      });
      setSubmittedOrder(response.data);
      setPayments([]);
      setActivePageTab("checkout");
      setActiveOperatorTab("payment");
      toast.success(`주문 #${response.data.id} 이 생성되었습니다.`);
      await loadOrderAndPayments(response.data.id, true);
    } catch (error) {
      toast.error(getErrorToastMessage(error, "주문 생성에 실패했습니다."));
    } finally {
      setSubmittingOrder(false);
    }
  }, [
    cartItems,
    customerCaptureDetail,
    customerCaptureMode,
    customerNameInput,
    customerPhoneInput,
    loadOrderAndPayments,
    memo,
    orderType,
    selectedTableId,
    submittedOrder,
    terminalCustomerCaptureAvailable,
    terminalCustomerPhoneInputAvailable,
    terminalCustomerQrAvailable,
  ]);

  const handleCreatePayment = useCallback(async () => {
    if (!submittedOrder) {
      toast.error("먼저 주문을 생성해주세요.");
      return;
    }
    if (!selectedTerminalId) {
      toast.error("결제 단말을 선택해주세요.");
      return;
    }

    const amount = Number(paymentForm.amount || submittedOrder.total_amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error("유효한 결제 금액을 입력해주세요.");
      return;
    }

    setCreatingPayment(true);
    try {
      await posService.createTableOrderPayment(submittedOrder.id, {
        terminal_id: Number(selectedTerminalId),
        amount,
        tax_amount: parseOptionalNumber(paymentForm.taxAmount),
        supply_amount: parseOptionalNumber(paymentForm.supplyAmount),
        tax_free_amount: parseOptionalNumber(paymentForm.taxFreeAmount),
        tip_amount: parseOptionalNumber(paymentForm.tipAmount),
      });
      toast.success("결제 요청을 단말로 보냈습니다.");
      await loadOrderAndPayments(submittedOrder.id, true);
      await loadWebPendingOrders(true);
    } catch (error) {
      toast.error(
        getErrorToastMessage(error, "결제 요청 생성에 실패했습니다."),
      );
    } finally {
      setCreatingPayment(false);
    }
  }, [loadOrderAndPayments, paymentForm, selectedTerminalId, submittedOrder]);

  const handleRequestCancel = useCallback(async () => {
    if (!latestSuccessfulPay || !submittedOrder) {
      toast.error("취소 가능한 결제 내역이 없습니다.");
      return;
    }

    setActingPaymentId(latestSuccessfulPay.id);
    try {
      await posService.requestPaymentCancel(latestSuccessfulPay.id, {
        reason: "CRM cashier 운영자 취소",
      });
      toast.success("취소 요청을 단말로 보냈습니다.");
      await loadOrderAndPayments(submittedOrder.id, true);
      await loadWebPendingOrders(true);
    } catch (error) {
      toast.error(getErrorToastMessage(error, "취소 요청에 실패했습니다."));
    } finally {
      setActingPaymentId(null);
    }
  }, [latestSuccessfulPay, loadOrderAndPayments, submittedOrder]);

  const handleRequestReprint = useCallback(async () => {
    if (!latestReprintTarget || !submittedOrder) {
      toast.error("재출력 가능한 영수증 이력이 없습니다.");
      return;
    }

    setActingPaymentId(latestReprintTarget.id);
    try {
      await posService.requestPaymentReprint(latestReprintTarget.id, {
        reason: "CRM cashier 영수증 재출력",
      });
      toast.success("영수증 재출력 요청을 보냈습니다.");
      await loadOrderAndPayments(submittedOrder.id, true);
    } catch (error) {
      toast.error(getErrorToastMessage(error, "재출력 요청에 실패했습니다."));
    } finally {
      setActingPaymentId(null);
    }
  }, [latestReprintTarget, loadOrderAndPayments, submittedOrder]);

  const handleLoadPreviousOrder = useCallback(
    async (order: TableOrder) => {
      if (submittedOrder?.id === order.id) {
        toast.success(`주문 #${order.id} 이 이미 열려 있습니다.`);
        return;
      }

      const hasExistingContext =
        cartItems.length > 0 || Boolean(submittedOrder);
      if (hasExistingContext) {
        const confirmed = await dialogService.confirm({
          title: "현재 POS 내용을 교체하시겠습니까?",
          description:
            "현재 장바구니와 주문 컨텍스트가 선택한 이전 주문으로 바뀝니다.",
          confirmLabel: "교체",
          cancelLabel: "유지",
        });
        if (!confirmed) {
          return;
        }
      }

      await loadOrderAndPayments(order.id);
      setActivePageTab("checkout");
      setActiveOperatorTab("payment");
      await loadWebPendingOrders(true);
      toast.success(`주문 #${order.id} 을 불러왔습니다.`);
    },
    [cartItems.length, loadOrderAndPayments, loadWebPendingOrders, submittedOrder],
  );

  const handleReopenPayment = useCallback(async () => {
    if (!submittedOrder) {
      return;
    }

    setPostCancelActionPending("REOPEN_PAYMENT");
    try {
      await posService.submitTableOrderPostCancelAction(submittedOrder.id, {
        action: "REOPEN_PAYMENT",
      });
      await loadOrderAndPayments(submittedOrder.id, true);
      await loadWebPendingOrders(true);
      setIsPostCancelDecisionOpen(false);
      setActivePageTab("checkout");
      setActiveOperatorTab("payment");
      toast.success("같은 주문으로 재결제를 다시 진행할 수 있습니다.");
    } catch (error) {
      toast.error(getErrorToastMessage(error, "재결제 준비에 실패했습니다."));
    } finally {
      setPostCancelActionPending(null);
    }
  }, [loadOrderAndPayments, loadWebPendingOrders, submittedOrder]);

  const handleCancelOrderRecord = useCallback(async () => {
    if (!submittedOrder) {
      return;
    }

    setPostCancelActionPending("CANCEL_ORDER");
    try {
      await posService.submitTableOrderPostCancelAction(submittedOrder.id, {
        action: "CANCEL_ORDER",
      });
      await loadWebPendingOrders(true);
      setIsPostCancelDecisionOpen(false);
      resetDraft();
      toast.success(`주문 #${submittedOrder.id} 이 주문취소 처리되었습니다.`);
    } catch (error) {
      toast.error(getErrorToastMessage(error, "주문취소 처리에 실패했습니다."));
    } finally {
      setPostCancelActionPending(null);
    }
  }, [loadWebPendingOrders, resetDraft, submittedOrder]);

  const canCreatePayment = Boolean(
    submittedOrder &&
    selectedTerminalId &&
    activeTerminals.length > 0 &&
    !hasPendingPayRequest &&
    (submittedOrder.payment_status === "UNPAID" ||
      submittedOrder.payment_status === "PAYMENT_FAILED"),
  );

  const latestPayment = payments[0] || null;
  const paymentPrimaryActionLabel =
    submittedOrder?.payment_status === "PAYMENT_FAILED"
      ? "재결제 요청 보내기"
      : "단말로 결제 요청 보내기";
  const normalizedCustomerPhone = useMemo(
    () => normalizePhoneInput(customerPhoneInput),
    [customerPhoneInput],
  );
  const submitOrderBlockReason = getSubmitOrderBlockReason({
    cartItemsLength: cartItems.length,
    customerCaptureMode,
    orderType,
    normalizedCustomerPhone,
    selectedTableId,
    submittedOrder,
    terminalCustomerCaptureAvailable,
  });
  const submitOrderButtonLabel = getSubmitOrderButtonLabel({
    submittingOrder,
    submitOrderBlockReason,
    submittedOrder,
  });
  const isSubmitOrderDisabled = Boolean(
    submittingOrder || submitOrderBlockReason,
  );
  const canShowOrderSummary = Boolean(submittedOrder || cartItems.length > 0);

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="캐셔 POS" pageTitle="현장주문" />

        <div className="flex h-[calc(100vh-160px)] max-h-[calc(100vh-160px)] min-h-0 flex-col gap-4 overflow-hidden">
          <div className="flex shrink-0 flex-wrap items-end justify-between gap-3">
            <div className="flex flex-wrap gap-4 border-b border-slate-200">
              {(Object.keys(pageTabLabels) as PageTab[]).map((tab) => (
                <button
                  type="button"
                  key={tab}
                  onClick={() => setActivePageTab(tab)}
                  className={`-mb-px border-b-2 px-1 pb-3 pt-1 text-sm font-medium transition-colors ${
                    activePageTab === tab
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent text-slate-500 hover:text-slate-700"
                  }`}
                >
                  {pageTabLabels[tab]}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap items-end justify-end gap-3">
              <div className="min-w-60 flex-1 sm:max-w-[320px]">
                <Label className="mb-2 block text-xs font-medium uppercase tracking-wide text-slate-500">
                  결제 단말
                </Label>
                <select
                  className="form-select min-w-60"
                  value={selectedTerminalId}
                  onChange={(
                    event: React.ChangeEvent<HTMLSelectElement>,
                  ) => setSelectedTerminalId(event.target.value)}
                  disabled={activeTerminals.length === 0}
                >
                  {activeTerminals.length === 0 ? (
                    <option value="">활성 단말 없음</option>
                  ) : null}
                  {activeTerminals.map((terminal) => (
                    <option key={terminal.id} value={String(terminal.id)}>
                      {getTerminalLabel(terminal)}
                    </option>
                  ))}
                </select>
              </div>
              <Button
                size="sm"
                color="soft-primary"
                onClick={() => setIsTerminalSettingsModalOpen(true)}
                className="shrink-0"
              >
                단말 설정
              </Button>
            </div>
          </div>

          {activePageTab === "compose" ? (
            <div className="grid min-h-0 flex-1 grid-cols-2 gap-4 xl:grid-cols-[minmax(0,1.5fr)_minmax(360px,1fr)]">
              <div className="min-h-0">
                <Card className="flex h-full min-h-0 flex-col">
                  <CardHeader className="d-flex flex-wrap items-center justify-content-between gap-3">
                    <div>
                      <h5 className="mb-1">주문 작성</h5>
                      <p className="text-muted mb-0">
                        주문 시 결제 진행 탭으로 이동합니다.
                      </p>
                    </div>
                    <div className="d-flex gap-2">
                      <Button
                        color="soft-primary"
                        onClick={() => void loadCatalog()}
                        disabled={loading}
                      >
                        새로고침
                      </Button>
                      <Button
                        color="light"
                        onClick={resetDraft}
                        disabled={submittingOrder || creatingPayment}
                      >
                        새 주문 시작
                      </Button>
                    </div>
                  </CardHeader>
                  <CardBody className="min-h-0 flex-1 overflow-y-auto">
                    <div className="grid min-h-0 grid-cols-1 gap-3 lg:grid-cols-[220px_minmax(0,1fr)]">
                      <div className="flex shrink-0 flex-col gap-3 rounded-3xl border border-slate-200 bg-slate-50 p-4">
                        <div>
                          <Label className="mb-2">주문 타입</Label>
                          <div className="flex gap-2">
                            {(
                              Object.keys(orderTypeLabels) as Array<
                                TableOrder["order_type"]
                              >
                            ).map((type) => (
                              <Button
                                key={type}
                                color={
                                  orderType === type
                                    ? "primary"
                                    : "soft-secondary"
                                }
                                onClick={() => setOrderType(type)}
                                className="flex-1"
                              >
                                {orderTypeLabels[type]}
                              </Button>
                            ))}
                          </div>
                        </div>

                        {orderType === "serving" ? (
                          <div className="space-y-3">
                            <div>
                              <Label className="mb-2">테이블 그룹</Label>
                              <select
                                className="form-select"
                                value={selectedTableGroupId}
                                onChange={(
                                  event: React.ChangeEvent<HTMLSelectElement>,
                                ) => {
                                  setSelectedTableGroupId(event.target.value);
                                  setSelectedTableId("");
                                }}
                              >
                                <option value="all">전체 그룹</option>
                                {tableGroups.map((group) => (
                                  <option
                                    key={group.id}
                                    value={String(group.id)}
                                  >
                                    {group.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <Label className="mb-2">테이블</Label>
                              <select
                                className="form-select"
                                value={selectedTableId}
                                onChange={(
                                  event: React.ChangeEvent<HTMLSelectElement>,
                                ) => setSelectedTableId(event.target.value)}
                              >
                                <option value="">테이블 선택</option>
                                {filteredTables.map((table) => (
                                  <option
                                    key={table.id}
                                    value={String(table.id)}
                                  >
                                    {table.group_name
                                      ? `${table.group_name} · `
                                      : ""}
                                    {table.name} ({table.code})
                                  </option>
                                ))}
                              </select>
                            </div>
                            {filteredTables.length === 0 ? (
                              <p className="mb-0 text-sm text-amber-600">
                                선택한 그룹에 활성 테이블이 없습니다.
                              </p>
                            ) : null}
                          </div>
                        ) : (
                          <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-3 text-sm text-slate-500">
                            픽업 주문은 테이블 없이 바로 생성됩니다.
                          </div>
                        )}

                        <div>
                          <Label className="mb-2">메모</Label>
                          <textarea
                            className="form-control"
                            rows={4}
                            value={memo}
                            onChange={(event) => setMemo(event.target.value)}
                            placeholder="주문 메모를 입력하세요."
                          />
                        </div>
                      </div>
                      <div className="flex min-h-0 flex-col gap-4">
                        <div className="flex shrink-0 flex-wrap gap-2">
                          <Button
                            color={
                              selectedCategoryId === "all"
                                ? "primary"
                                : "soft-secondary"
                            }
                            onClick={() => setSelectedCategoryId("all")}
                            size="sm"
                          >
                            전체
                          </Button>
                          {categories.map((category) => (
                            <Button
                              key={category.id}
                              color={
                                selectedCategoryId === String(category.id)
                                  ? "primary"
                                  : "soft-secondary"
                              }
                              onClick={() =>
                                setSelectedCategoryId(String(category.id))
                              }
                              size="sm"
                            >
                              {category.name}
                            </Button>
                          ))}
                        </div>

                        <div className="min-h-0 overflow-y-auto pr-1">
                          {loading ? (
                            <div className="rounded-3xl border border-slate-200 bg-white p-6 text-center text-muted">
                              POS 데이터를 불러오는 중입니다.
                            </div>
                          ) : filteredMenus.length === 0 ? (
                            <div className="rounded-3xl border border-dashed border-slate-200 bg-white p-6 text-center text-muted">
                              표시할 메뉴가 없습니다.
                            </div>
                          ) : (
                            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 2xl:grid-cols-3">
                              {filteredMenus.map((menu) => {
                                const optionGroups = getMenuOptionGroups(menu);
                                return (
                                  <button
                                    key={menu.id}
                                    type="button"
                                    className="rounded-3xl border border-slate-200 bg-white p-4 text-left transition hover:border-blue-300 hover:shadow-sm disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
                                    onClick={() => openMenuPicker(menu)}
                                    disabled={
                                      menu.is_sold_out ||
                                      Boolean(submittedOrder)
                                    }
                                  >
                                    <div className="mb-3 overflow-hidden rounded-2xl border border-slate-200 bg-slate-50">
                                      {menu.image ? (
                                        <img
                                          src={menu.image}
                                          alt={menu.name}
                                          className="h-36 w-full object-cover"
                                        />
                                      ) : (
                                        <div className="flex h-36 w-full items-center justify-center bg-[linear-gradient(135deg,#f8fafc,#e2e8f0)] text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                                          No Image
                                        </div>
                                      )}
                                    </div>
                                    <div className="mb-2 flex items-start justify-between gap-2">
                                      <div className="min-w-0">
                                        <h6 className="mb-1 text-base font-semibold text-slate-800">
                                          {menu.name}
                                        </h6>
                                        <p className="mb-0 line-clamp-2 text-sm text-slate-500">
                                          {menu.description || "설명 없음"}
                                        </p>
                                        <p className="mt-2 mb-0 text-xs font-medium text-slate-500">
                                          {fulfillmentTypeLabels[menu.fulfillment_type]}
                                        </p>
                                      </div>
                                      {menu.is_sold_out ? (
                                        <span className="rounded-full bg-red-100 px-2 py-1 text-xs font-medium text-red-700">
                                          품절
                                        </span>
                                      ) : null}
                                    </div>
                                    <div className="flex items-center justify-between text-sm">
                                      <span className="font-semibold text-slate-700">
                                        {formatCurrency(menu.price)}
                                      </span>
                                      <span className="text-slate-500">
                                        {optionGroups.length > 0
                                          ? `${optionGroups.length}개 옵션 그룹`
                                          : "옵션 없음"}
                                      </span>
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </div>

              <div className="min-h-0 overflow-y-auto pr-1">
                <div className="space-y-4">
                  <Card>
                    <CardBody className="space-y-4">
                      <div>
                        <Label className="mb-2">고객 정보</Label>
                        <div className="grid grid-cols-3 gap-2">
                          {orderedCustomerCaptureModes.map((mode) => {
                            const isTerminalMode = mode === "TOSS_FRONT";
                            const disabled =
                              isTerminalMode &&
                              !terminalCustomerCaptureAvailable;
                            return (
                              <Button
                                key={mode}
                                color={
                                  customerCaptureMode === mode
                                    ? "primary"
                                    : "soft-secondary"
                                }
                                onClick={() => {
                                  if (disabled) {
                                    return;
                                  }
                                  setHasTouchedCustomerCaptureMode(true);
                                  setCustomerCaptureMode(mode);
                                  if (mode === "CRM_DIRECT") {
                                    setCustomerCaptureDetail("MANUAL");
                                  }
                                  if (mode === "TOSS_FRONT") {
                                    setCustomerPhoneInput("");
                                    setCustomerNameInput("");
                                    setCustomerCaptureDetail(
                                      getPreferredTerminalCaptureDetail({
                                        terminalCustomerPhoneInputAvailable,
                                        terminalCustomerQrAvailable,
                                      }),
                                    );
                                  }
                                  if (mode === "SKIP") {
                                    setCustomerCaptureDetail("NONE");
                                    setCustomerPhoneInput("");
                                    setCustomerNameInput("");
                                  }
                                }}
                                disabled={disabled || Boolean(submittedOrder)}
                                className="justify-center"
                              >
                                {customerCaptureModeLabels[mode]}
                              </Button>
                            );
                          })}
                        </div>
                        {!terminalCustomerCaptureAvailable ? (
                          <p className="mt-2 mb-0 text-xs text-amber-600">
                            현재 연결된 단말은 전화번호 입력 또는 회원 QR 캡처를
                            지원하지 않습니다. 직접 입력이나 전화번호 없이
                            진행을 선택해주세요.
                          </p>
                        ) : null}
                      </div>

                      {customerCaptureMode === "CRM_DIRECT" ? (
                        <>
                          <div>
                            <Label className="mb-2">고객 전화번호</Label>
                            <Input
                              value={customerPhoneInput}
                              onChange={(
                                event: React.ChangeEvent<HTMLInputElement>,
                              ) =>
                                setCustomerPhoneInput(
                                  event.target.value.replace(/[^0-9-]/g, ""),
                                )
                              }
                              placeholder="예: 01012345678"
                              disabled={Boolean(submittedOrder)}
                            />
                          </div>
                          <div>
                            <Label className="mb-2">고객 이름/표시명</Label>
                            <Input
                              value={customerNameInput}
                              onChange={(
                                event: React.ChangeEvent<HTMLInputElement>,
                              ) => setCustomerNameInput(event.target.value)}
                              placeholder="예: 김고객"
                              disabled={Boolean(submittedOrder)}
                            />
                          </div>
                        </>
                      ) : null}

                      {customerCaptureMode === "TOSS_FRONT" ? (
                        <div className="text-sm text-slate-600">
                          * 고객이 전화번호 또는 QR (마이페이지-더보기) 스캔을 선택
                        </div>
                      ) : null}
                    </CardBody>
                  </Card>

                  <Card>
                    <CardHeader>
                      <h5 className="mb-1">장바구니</h5>
                      <p className="text-muted mb-0">
                        주문 생성 전까지 수량과 항목을 조정할 수 있습니다.
                      </p>
                    </CardHeader>
                    <CardBody className="space-y-4">
                      {cartItems.length === 0 ? (
                        <div className="rounded-3xl border border-dashed border-slate-200 bg-slate-50 p-5 text-center text-sm text-slate-500">
                          아직 담긴 메뉴가 없습니다.
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {cartItems.map((item, index) => (
                            <div
                              key={item.id}
                              className="rounded-3xl border border-slate-200 p-4"
                            >
                              <div className="mb-2 flex items-start justify-between gap-2">
                                <div>
                                  <h6 className="mb-1 text-sm font-semibold text-slate-800">
                                    {item.menu_name_snapshot}
                                  </h6>
                                  <p className="mb-0 text-xs text-slate-500">
                                    {fulfillmentTypeLabels[item.fulfillment_type]} ·{" "}
                                    {item.options.length === 0
                                      ? "옵션 없음"
                                      : item.options
                                          .map(
                                            (option) =>
                                              `${option.option_name_snapshot}(+${option.extra_price_snapshot.toLocaleString()})`,
                                          )
                                          .join(", ")}
                                  </p>
                                </div>
                                <Button
                                  color="soft-danger"
                                  size="sm"
                                  onClick={() => removeCartItem(index)}
                                  disabled={Boolean(submittedOrder)}
                                >
                                  삭제
                                </Button>
                              </div>
                              <div className="flex items-center justify-between gap-3">
                                <div className="flex items-center gap-2">
                                  <Button
                                    color="soft-secondary"
                                    size="sm"
                                    onClick={() =>
                                      updateCartItemQuantity(
                                        index,
                                        item.quantity - 1,
                                      )
                                    }
                                    disabled={
                                      item.quantity <= 1 ||
                                      Boolean(submittedOrder)
                                    }
                                  >
                                    -
                                  </Button>
                                  <span className="min-w-8 text-center text-sm font-medium">
                                    {item.quantity}
                                  </span>
                                  <Button
                                    color="soft-secondary"
                                    size="sm"
                                    onClick={() =>
                                      updateCartItemQuantity(
                                        index,
                                        item.quantity + 1,
                                      )
                                    }
                                    disabled={Boolean(submittedOrder)}
                                  >
                                    +
                                  </Button>
                                </div>
                                <strong className="text-slate-800">
                                  {formatCurrency(item.line_amount)}
                                </strong>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="rounded-3xl bg-slate-50 p-4">
                        <div className="mb-2 flex items-center justify-between text-sm">
                          <span className="text-slate-500">주문 타입</span>
                          <span className="font-medium text-slate-700">
                            {orderTypeLabels[orderType]}
                          </span>
                        </div>
                        <div className="mb-2 flex items-center justify-between text-sm">
                          <span className="text-slate-500">제공 타입</span>
                          <span className="font-medium text-slate-700">
                            {cartItems[0]
                              ? fulfillmentTypeLabels[
                                  cartItems[0].fulfillment_type
                                ]
                              : "-"}
                          </span>
                        </div>
                        <div className="mb-2 flex items-center justify-between text-sm">
                          <span className="text-slate-500">선택 테이블</span>
                          <span className="font-medium text-slate-700">
                            {orderType === "pickup"
                              ? "픽업"
                              : selectedTable
                                ? `${selectedTable.name} (${selectedTable.code})`
                                : "미선택"}
                          </span>
                        </div>
                        <div className="mb-2 flex items-center justify-between text-sm">
                          <span className="text-slate-500">고객 수집 방식</span>
                          <span className="font-medium text-slate-700">
                            {customerCaptureModeLabels[customerCaptureMode]}
                          </span>
                        </div>
                        {customerCaptureMode !== "SKIP" ? (
                          <>
                            <div className="mb-2 flex items-center justify-between text-sm">
                              <span className="text-slate-500">
                                고객 전화번호
                              </span>
                              <span className="font-medium text-slate-700">
                                {customerCaptureMode === "CRM_DIRECT"
                                  ? customerPhoneInput || "-"
                                  : "결제 단말에서 입력"}
                              </span>
                            </div>
                            <div className="mb-2 flex items-center justify-between text-sm">
                              <span className="text-slate-500">
                                고객 표시명
                              </span>
                              <span className="font-medium text-slate-700">
                                {customerCaptureMode === "CRM_DIRECT"
                                  ? customerNameInput || "-"
                                  : "단말 선택 후 수집"}
                              </span>
                            </div>
                          </>
                        ) : null}
                        <div className="flex items-center justify-between text-base font-semibold text-slate-800">
                          <span>총 금액</span>
                          <span>{formatCurrency(cartTotal)}</span>
                        </div>
                      </div>

                      <Button
                        color={
                          submitOrderBlockReason ? "soft-secondary" : "primary"
                        }
                        className="w-full"
                        onClick={handleSubmitOrder}
                        disabled={isSubmitOrderDisabled}
                      >
                        {submitOrderButtonLabel}
                      </Button>
                      {submitOrderBlockReason ? (
                        <p className="mb-0 text-sm text-amber-600">
                          {submitOrderBlockReason}
                        </p>
                      ) : null}
                    </CardBody>
                  </Card>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 xl:grid-cols-[minmax(320px,0.85fr)_minmax(0,1.15fr)]">
              <div className="min-h-0 overflow-y-auto pr-1">
                <div className="space-y-4">
                  <Card>
                    <CardHeader className="d-flex flex-wrap items-center justify-content-between gap-3">
                      <div>
                        <h5 className="mb-1">주문 확인</h5>
                        <p className="text-muted mb-0">
                          생성된 주문을 확인하고 결제 진행 전 현재 상태를
                          검토합니다.
                        </p>
                      </div>
                      <div className="d-flex gap-2">
                        <Button
                          color="soft-secondary"
                          onClick={() => setActivePageTab("compose")}
                        >
                          주문 작성 탭으로 이동
                        </Button>
                        <Button
                          color="light"
                          onClick={resetDraft}
                          disabled={submittingOrder || creatingPayment}
                        >
                          새 주문 시작
                        </Button>
                      </div>
                    </CardHeader>
                    <CardBody className="space-y-4">
                      {!canShowOrderSummary ? (
                        <div className="rounded-3xl border border-dashed border-slate-200 bg-slate-50 p-5 text-center text-sm text-slate-500">
                          아직 확인할 주문이 없습니다. 이전 주문을 불러오거나
                          주문 작성 탭에서 새 주문을 생성해주세요.
                        </div>
                      ) : (
                        <>
                          <div className="rounded-3xl bg-slate-50 p-4">
                            <div className="mb-2 flex items-center justify-between text-sm">
                              <span className="text-slate-500">주문 번호</span>
                              <span className="font-medium text-slate-700">
                                {submittedOrder
                                  ? `#${submittedOrder.id}`
                                  : "미생성 드래프트"}
                              </span>
                            </div>
                            <div className="mb-2 flex items-center justify-between text-sm">
                              <span className="text-slate-500">주문 타입</span>
                              <span className="font-medium text-slate-700">
                                {
                                  orderTypeLabels[
                                    submittedOrder?.order_type || orderType
                                  ]
                                }
                              </span>
                            </div>
                            <div className="mb-2 flex items-center justify-between text-sm">
                              <span className="text-slate-500">제공 타입</span>
                              <span className="font-medium text-slate-700">
                                {submittedOrder?.fulfillment_type
                                  ? fulfillmentTypeLabels[
                                      submittedOrder.fulfillment_type
                                    ]
                                  : cartItems[0]
                                    ? fulfillmentTypeLabels[
                                        cartItems[0].fulfillment_type
                                      ]
                                    : "-"}
                              </span>
                            </div>
                            <div className="mb-2 flex items-center justify-between text-sm">
                              <span className="text-slate-500">
                                선택 테이블
                              </span>
                              <span className="font-medium text-slate-700">
                                {submittedOrder?.table_name ||
                                  (submittedOrder?.order_type === "pickup"
                                    ? "픽업"
                                    : orderType === "pickup"
                                      ? "픽업"
                                      : selectedTable
                                        ? `${selectedTable.name} (${selectedTable.code})`
                                        : "미선택")}
                              </span>
                            </div>
                            <div className="mb-2 flex items-center justify-between text-sm">
                              <span className="text-slate-500">
                                고객 수집 방식
                              </span>
                              <span className="font-medium text-slate-700">
                                {
                                  customerCaptureModeLabels[
                                    submittedOrder?.customer_phone_capture_mode ||
                                      customerCaptureMode
                                  ]
                                }
                              </span>
                            </div>
                            <div className="mb-2 flex items-center justify-between text-sm">
                              <span className="text-slate-500">
                                고객 전화번호
                              </span>
                              <span className="font-medium text-slate-700">
                                {submittedOrder?.customer_phone_num ||
                                  (customerCaptureMode === "CRM_DIRECT"
                                    ? customerPhoneInput || "-"
                                    : customerCaptureMode === "TOSS_FRONT"
                                      ? "결제 단말에서 입력"
                                      : "-")}
                              </span>
                            </div>
                            <div className="flex items-center justify-between text-base font-semibold text-slate-800">
                              <span>총 금액</span>
                              <span>
                                {formatCurrency(
                                  submittedOrder?.total_amount || cartTotal,
                                )}
                              </span>
                            </div>
                          </div>

                          <div className="space-y-3">
                            {cartItems.length === 0 ? (
                              <div className="rounded-3xl border border-dashed border-slate-200 bg-slate-50 p-5 text-center text-sm text-slate-500">
                                표시할 주문 항목이 없습니다.
                              </div>
                            ) : (
                              cartItems.map((item) => (
                                <div
                                  key={item.id}
                                  className="rounded-3xl border border-slate-200 p-4"
                                >
                                  <div className="mb-2 flex items-start justify-between gap-2">
                                    <div>
                                      <h6 className="mb-1 text-sm font-semibold text-slate-800">
                                        {item.menu_name_snapshot}
                                      </h6>
                                      <p className="mb-0 text-xs text-slate-500">
                                        {item.options.length === 0
                                          ? "옵션 없음"
                                          : item.options
                                              .map(
                                                (option) =>
                                                  `${option.option_name_snapshot}(+${option.extra_price_snapshot.toLocaleString()})`,
                                              )
                                              .join(", ")}
                                      </p>
                                    </div>
                                    <span className="text-sm text-slate-500">
                                      {item.quantity}개
                                    </span>
                                  </div>
                                  <div className="flex items-center justify-between text-sm">
                                    <span className="text-slate-500">
                                      라인 합계
                                    </span>
                                    <strong className="text-slate-800">
                                      {formatCurrency(item.line_amount)}
                                    </strong>
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        </>
                      )}
                    </CardBody>
                  </Card>
                </div>
              </div>

              <div className="min-h-0 overflow-y-auto pr-1">
                <div className="space-y-4">
                  <Card>
                    <CardHeader className="d-flex flex-wrap items-center justify-content-between gap-3 pb-0">
                      <div className="flex flex-wrap gap-4 border-b border-slate-200">
                        {(
                          Object.keys(
                            operatorPanelTabLabels,
                          ) as OperatorPanelTab[]
                        ).map((tab) => (
                              <button
                                type="button"
                            key={tab}
                            onClick={() => setActiveOperatorTab(tab)}
                                className={`-mb-px border-b-2 px-1 pb-3 pt-1 text-sm font-medium transition-colors ${
                                  activeOperatorTab === tab
                                    ? "border-blue-500 text-blue-600"
                                    : "border-transparent text-slate-500 hover:text-slate-700"
                                }`}
                          >
                            {operatorPanelTabLabels[tab]}
                            {tab === "history" ? ` ${payments.length}` : ""}
                            {tab === "webPending"
                              ? ` ${webPendingOrders.length}`
                              : ""}
                              </button>
                        ))}
                      </div>
                    </CardHeader>
                    <CardBody className="space-y-4">
                      {activeOperatorTab === "payment" ? (
                        !submittedOrder ? (
                          <div className="rounded-3xl border border-dashed border-slate-200 bg-slate-50 p-5 text-center text-sm text-slate-500">
                            먼저 주문을 생성하거나 이전 주문을 불러와주세요.
                          </div>
                        ) : (
                          <>
                            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                              <div className="rounded-3xl border border-slate-200 p-4">
                                <p className="mb-1 text-sm text-slate-500">
                                  주문 번호
                                </p>
                                <h4 className="mb-0 text-slate-800">
                                  #{submittedOrder.id}
                                </h4>
                              </div>
                              <div className="rounded-3xl border border-slate-200 p-4">
                                <p className="mb-1 text-sm text-slate-500">
                                  결제 상태
                                </p>
                                <div
                                  className={`inline-flex rounded-full px-3 py-1 text-sm font-medium ${statusPillClassName(submittedOrder.payment_status)}`}
                                >
                                  {
                                    paymentStatusLabels[
                                      submittedOrder.payment_status
                                    ]
                                  }
                                </div>
                              </div>
                            </div>

                            <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
                              <div className="mb-2 flex items-center justify-between gap-2">
                                <div>
                                  <p className="mb-1 text-sm text-slate-500">
                                    고객 정보
                                  </p>
                                  <h6 className="mb-0 text-slate-800">
                                    {submittedOrder.customer_name ||
                                      submittedOrder.customer_phone_num ||
                                      "전화번호 없이 진행"}
                                  </h6>
                                </div>
                                <span
                                  className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${statusPillClassName(submittedOrder.customer_registration_state === "NONE" ? "CANCELED" : submittedOrder.customer_registration_state === "AUTO_CREATED_USER" ? "PROCESSING" : "PAID")}`}
                                >
                                  {
                                    customerRegistrationLabels[
                                      submittedOrder.customer_registration_state
                                    ]
                                  }
                                </span>
                              </div>
                              <div className="grid grid-cols-1 gap-2 text-sm text-slate-600 md:grid-cols-2">
                                <div>
                                  전화번호:{" "}
                                  {submittedOrder.customer_phone_num || "-"}
                                </div>
                                <div>
                                  수집방식:{" "}
                                  {
                                    customerCaptureModeLabels[
                                      submittedOrder.customer_phone_capture_mode
                                    ]
                                  }
                                </div>
                                <div>
                                  세부방식:{" "}
                                  {
                                    customerCaptureDetailLabels[
                                      submittedOrder
                                        .customer_phone_capture_detail
                                    ]
                                  }
                                </div>
                                <div>
                                  입력시각:{" "}
                                  {formatDateTime(
                                    submittedOrder.customer_phone_captured_at,
                                  )}
                                </div>
                              </div>
                            </div>

                            {activeTerminals.length === 0 ? (
                              <p className="mb-0 text-sm text-red-500">
                                활성 단말이 없습니다. 단말 설정에서 먼저 페어링을 완료하세요.
                              </p>
                            ) : null}

                            <div>
                              <Label className="mb-2">결제 금액</Label>
                              <Input
                                value={paymentForm.amount}
                                onChange={(
                                  event: React.ChangeEvent<HTMLInputElement>,
                                ) =>
                                  setPaymentForm((current) => ({
                                    ...current,
                                    amount: event.target.value.replace(
                                      /[^0-9]/g,
                                      "",
                                    ),
                                  }))
                                }
                                placeholder="결제 금액"
                              />
                              <p className="mt-2 mb-0 text-sm text-slate-500">
                                기본값은 주문 총액입니다. 필요하면
                                세금/공급가/면세/팁을 별도로 입력할 수 있습니다.
                              </p>
                            </div>

                            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                              <div>
                                <Label className="mb-2">과세 금액</Label>
                                <Input
                                  value={paymentForm.taxAmount}
                                  onChange={(
                                    event: React.ChangeEvent<HTMLInputElement>,
                                  ) =>
                                    setPaymentForm((current) => ({
                                      ...current,
                                      taxAmount: event.target.value.replace(
                                        /[^0-9]/g,
                                        "",
                                      ),
                                    }))
                                  }
                                  placeholder="예: 1000"
                                />
                              </div>
                              <div>
                                <Label className="mb-2">공급가액</Label>
                                <Input
                                  value={paymentForm.supplyAmount}
                                  onChange={(
                                    event: React.ChangeEvent<HTMLInputElement>,
                                  ) =>
                                    setPaymentForm((current) => ({
                                      ...current,
                                      supplyAmount: event.target.value.replace(
                                        /[^0-9]/g,
                                        "",
                                      ),
                                    }))
                                  }
                                  placeholder="예: 9000"
                                />
                              </div>
                              <div>
                                <Label className="mb-2">면세 금액</Label>
                                <Input
                                  value={paymentForm.taxFreeAmount}
                                  onChange={(
                                    event: React.ChangeEvent<HTMLInputElement>,
                                  ) =>
                                    setPaymentForm((current) => ({
                                      ...current,
                                      taxFreeAmount: event.target.value.replace(
                                        /[^0-9]/g,
                                        "",
                                      ),
                                    }))
                                  }
                                  placeholder="없으면 비워두기"
                                />
                              </div>
                              <div>
                                <Label className="mb-2">팁</Label>
                                <Input
                                  value={paymentForm.tipAmount}
                                  onChange={(
                                    event: React.ChangeEvent<HTMLInputElement>,
                                  ) =>
                                    setPaymentForm((current) => ({
                                      ...current,
                                      tipAmount: event.target.value.replace(
                                        /[^0-9]/g,
                                        "",
                                      ),
                                    }))
                                  }
                                  placeholder="없으면 비워두기"
                                />
                              </div>
                            </div>

                            <div className="flex flex-wrap gap-2">
                              <Button
                                color="primary"
                                onClick={handleCreatePayment}
                                disabled={!canCreatePayment || creatingPayment}
                              >
                                {creatingPayment
                                  ? "전송 중..."
                                  : paymentPrimaryActionLabel}
                              </Button>
                              <Button
                                color="soft-primary"
                                onClick={() =>
                                  submittedOrder &&
                                  void loadOrderAndPayments(
                                    submittedOrder.id,
                                    true,
                                  )
                                }
                                disabled={!submittedOrder}
                              >
                                상태 새로고침
                              </Button>
                              <Button
                                color="soft-danger"
                                onClick={handleRequestCancel}
                                disabled={
                                  !latestSuccessfulPay ||
                                  submittedOrder.payment_status !== "PAID" ||
                                  actingPaymentId === latestSuccessfulPay?.id
                                }
                              >
                                결제 취소 요청
                              </Button>
                              <Button
                                color="soft-secondary"
                                onClick={handleRequestReprint}
                                disabled={
                                  !latestReprintTarget ||
                                  actingPaymentId === latestReprintTarget?.id
                                }
                              >
                                영수증 재출력
                              </Button>
                            </div>

                            <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
                              <div className="mb-2 flex items-center justify-between text-sm">
                                <span className="text-slate-500">
                                  최근 결제 요청
                                </span>
                                <span className="font-medium text-slate-700">
                                  {formatDateTime(
                                    submittedOrder.payment_requested_at,
                                  )}
                                </span>
                              </div>
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-slate-500">
                                  최근 상태 갱신
                                </span>
                                <span className="font-medium text-slate-700">
                                  {formatDateTime(submittedOrder.updated_at)}
                                </span>
                              </div>
                            </div>

                            {submittedOrder.payment_status ===
                            "PAYMENT_FAILED" ? (
                              <div className="rounded-3xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                                <p className="mb-1 font-semibold">
                                  최근 결제가 실패했습니다.
                                </p>
                                <p className="mb-0">
                                  {submittedOrder.payment_failure_reason ||
                                    latestPayment?.error_message ||
                                    "상세 오류 메시지가 없습니다."}
                                </p>
                              </div>
                            ) : null}

                            {submittedOrder.payment_status === "CANCELED" &&
                            submittedOrder.status !== "CANCELED" ? (
                              <div className="rounded-3xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-700">
                                <div className="flex flex-wrap items-center justify-between gap-3">
                                  <div>
                                    <p className="mb-1 font-semibold">
                                      결제 취소 후 후속 처리가 필요합니다.
                                    </p>
                                    <p className="mb-0">
                                      같은 주문으로 재결제를 이어가거나 주문
                                      내역까지 취소해야 active POS 흐름이
                                      정리됩니다.
                                    </p>
                                  </div>
                                  <Button
                                    color="soft-warning"
                                    onClick={() =>
                                      setIsPostCancelDecisionOpen(true)
                                    }
                                  >
                                    취소 후 처리 선택
                                  </Button>
                                </div>
                              </div>
                            ) : null}
                          </>
                        )
                      ) : null}

                      {activeOperatorTab === "history" ? (
                        <>
                          <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                            PAY, CANCEL, REPRINT 요청 상태를 최근순으로
                            보여줍니다.
                          </div>
                          {payments.length === 0 ? (
                            <div className="rounded-3xl border border-dashed border-slate-200 bg-slate-50 p-5 text-center text-sm text-slate-500">
                              아직 결제 요청 이력이 없습니다.
                            </div>
                          ) : (
                            <div className="space-y-3">
                              {payments.map((payment) => (
                                <div
                                  key={payment.id}
                                  className="rounded-3xl border border-slate-200 p-4"
                                >
                                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                                    <div className="flex items-center gap-2">
                                      <strong className="text-slate-800">
                                        {
                                          requestTypeLabels[
                                            payment.request_type
                                          ]
                                        }
                                      </strong>
                                      <span
                                        className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${statusPillClassName(payment.status)}`}
                                      >
                                        {
                                          paymentRequestStatusLabels[
                                            payment.status
                                          ]
                                        }
                                      </span>
                                    </div>
                                    <span className="text-xs text-slate-500">
                                      {formatDateTime(payment.requested_at)}
                                    </span>
                                  </div>
                                  <div className="grid grid-cols-1 gap-2 text-sm text-slate-600 md:grid-cols-2">
                                    <div>
                                      금액: {formatCurrency(payment.amount)}
                                    </div>
                                    <div>
                                      단말:{" "}
                                      {payment.terminal_label ||
                                        payment.terminal}
                                    </div>
                                    <div>
                                      과세 금액:{" "}
                                      {formatCurrency(payment.tax_amount)}
                                    </div>
                                    <div>
                                      공급가액:{" "}
                                      {formatCurrency(payment.supply_amount)}
                                    </div>
                                    <div>
                                      면세 금액:{" "}
                                      {formatCurrency(payment.tax_free_amount)}
                                    </div>
                                    <div>
                                      팁: {formatCurrency(payment.tip_amount)}
                                    </div>
                                    <div>
                                      결제키: {payment.payment_key || "-"}
                                    </div>
                                    <div>
                                      승인번호: {payment.approval_number || "-"}
                                    </div>
                                    <div>
                                      완료시각:{" "}
                                      {formatDateTime(payment.completed_at)}
                                    </div>
                                    <div>
                                      에러: {payment.error_message || "-"}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      ) : null}

                      {activeOperatorTab === "previous" ? (
                        <PreviousOrderLookupModal
                          embedded
                          isOpen={activeOperatorTab === "previous"}
                          onClose={() => setActiveOperatorTab("payment")}
                          onSelectOrder={handleLoadPreviousOrder}
                          tables={tables}
                          currentOrderId={submittedOrder?.id || null}
                        />
                      ) : null}

                      {activeOperatorTab === "webPending" ? (
                        <>
                          <div className="flex flex-wrap items-start justify-between gap-3 rounded-3xl border border-slate-200 bg-slate-50 p-4">
                            <div>
                              <p className="mb-1 text-sm font-medium text-slate-700">
                                오늘 생성된 웹 주문 중 미결제 또는 결제실패 상태만 표시합니다.
                              </p>
                              <p className="mb-0 text-sm text-slate-500">
                                주문을 불러오면 기존 POS 결제 흐름으로 바로 이어집니다.
                              </p>
                            </div>
                            <Button
                              color="soft-primary"
                              onClick={() => void loadWebPendingOrders()}
                              disabled={webPendingLoading || webPendingRefreshing}
                            >
                              {webPendingLoading || webPendingRefreshing
                                ? "새로고침 중..."
                                : "목록 새로고침"}
                            </Button>
                          </div>

                          {webPendingLoading ? (
                            <div className="rounded-3xl border border-dashed border-slate-200 bg-slate-50 p-5 text-center text-sm text-slate-500">
                              웹 주문 결제대기 목록을 불러오는 중입니다.
                            </div>
                          ) : webPendingOrders.length === 0 ? (
                            <div className="rounded-3xl border border-dashed border-slate-200 bg-slate-50 p-5 text-center text-sm text-slate-500">
                              현재 사이트 기준 오늘의 미결제 웹 주문이 없습니다.
                            </div>
                          ) : (
                            <div className="space-y-3">
                              {webPendingOrders.map((order) => {
                                const isCurrentOrder = submittedOrder?.id === order.id;
                                const customerLabel =
                                  order.customer_name ||
                                  order.customer_phone_num ||
                                  "고객 정보 없음";

                                return (
                                  <div
                                    key={order.id}
                                    className="rounded-3xl border border-slate-200 p-4"
                                  >
                                    <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
                                      <div>
                                        <div className="mb-1 flex flex-wrap items-center gap-2">
                                          <strong className="text-slate-800">#{order.id}</strong>
                                          <span className="text-sm text-slate-500">
                                            {formatDateTime(order.created_at)}
                                          </span>
                                        </div>
                                        <div className="text-sm text-slate-600">
                                          {fulfillmentTypeLabels[order.fulfillment_type || "KITCHEN"]} · {orderTypeLabels[order.order_type]} · {order.table_name || (order.order_type === "pickup" ? "픽업 주문" : "테이블 미지정")}
                                        </div>
                                        <div className="text-sm text-slate-600">{customerLabel}</div>
                                      </div>
                                      <div className="text-end">
                                        <div className="mb-2 font-semibold text-slate-800">
                                          {formatCurrency(order.total_amount)}
                                        </div>
                                        <div className="flex flex-wrap justify-end gap-2">
                                          <span
                                            className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${statusPillClassName(order.payment_status)}`}
                                          >
                                            {paymentStatusLabels[order.payment_status]}
                                          </span>
                                          <span
                                            className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${statusPillClassName(order.status)}`}
                                          >
                                            {orderStatusLabels[order.status]}
                                          </span>
                                        </div>
                                      </div>
                                    </div>

                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                      <div className="text-xs text-slate-500">
                                        출처: 웹 주문 · 결제대기 {webPendingRefreshing ? "· 동기화 중" : ""}
                                      </div>
                                      <Button
                                        color={isCurrentOrder ? "soft-secondary" : "soft-primary"}
                                        onClick={() => void handleLoadPreviousOrder(order)}
                                        disabled={isCurrentOrder}
                                      >
                                        {isCurrentOrder ? "현재 주문" : "불러오기"}
                                      </Button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </>
                      ) : null}
                    </CardBody>
                  </Card>
                </div>
              </div>
            </div>
          )}
        </div>

        <Modal
          isOpen={isTerminalSettingsModalOpen}
          onClose={() => setIsTerminalSettingsModalOpen(false)}
          variant="fullscreen"
          className="max-w-[100vw]! sm:max-w-[100vw]!"
        >
          <ModalHeader toggle={() => setIsTerminalSettingsModalOpen(false)}>
            단말 설정
          </ModalHeader>
          <ModalBody className="bg-slate-50 px-4! py-4! sm:px-6!">
            <div className="mx-auto flex w-full max-w-7xl flex-col gap-4">
              <div className="flex flex-wrap items-start justify-between gap-3 rounded-3xl border border-slate-200 bg-white p-5">
                <div>
                  <h5 className="mb-1 text-slate-800">캐셔 POS 단말 설정</h5>
                  <p className="mb-0 text-sm text-slate-500">
                    페어링, 상태 조회, 비활성화와 회수를 주문 화면을 벗어나지
                    않고 풀페이지에서 관리합니다.
                  </p>
                </div>
                <span className="rounded-full bg-slate-100 px-3 py-2 text-sm text-slate-600">
                  활성 {activeTerminals.length} / 전체 {terminals.length}
                </span>
              </div>

              <TerminalSettingsPanel
                initialTerminals={terminals}
                onTerminalsUpdated={handleTerminalsUpdated}
              />
            </div>
          </ModalBody>
        </Modal>

        <Modal
          isOpen={Boolean(menuPicker)}
          onClose={closeMenuPicker}
          title="메뉴 옵션 선택"
          variant="dialog"
          size="3xl"
        >
          {menuPicker ? (
            <>
              <ModalHeader toggle={closeMenuPicker}>
                {menuPicker.name}
              </ModalHeader>
              <ModalBody className="space-y-4">
                <div>
                  <p className="mb-1 text-sm text-slate-500">기본가</p>
                  <h4 className="mb-0 text-slate-800">
                    {formatCurrency(menuPicker.price)}
                  </h4>
                  <p className="mt-2 mb-0 text-sm text-slate-500">
                    {menuPicker.description || "설명 없음"}
                  </p>
                </div>

                <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="mb-1 text-sm font-medium text-slate-700">
                        수량
                      </p>
                      <p className="mb-0 text-xs text-slate-500">
                        옵션 금액은 수량에 맞춰 함께 반영됩니다.
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        color="soft-secondary"
                        size="sm"
                        onClick={() =>
                          setMenuPickerQuantity((current) =>
                            Math.max(1, current - 1),
                          )
                        }
                      >
                        -
                      </Button>
                      <span className="min-w-8 text-center text-sm font-semibold">
                        {menuPickerQuantity}
                      </span>
                      <Button
                        color="soft-secondary"
                        size="sm"
                        onClick={() =>
                          setMenuPickerQuantity((current) => current + 1)
                        }
                      >
                        +
                      </Button>
                    </div>
                  </div>
                </div>

                {getMenuOptionGroups(menuPicker).length === 0 ? (
                  <div className="rounded-3xl border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
                    선택할 옵션이 없습니다. 바로 장바구니에 추가할 수 있습니다.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {getMenuOptionGroups(menuPicker).map((group) => {
                      const selectedIds = menuPickerOptions[group.id] || [];
                      return (
                        <div
                          key={group.id}
                          className="rounded-3xl border border-slate-200 p-4"
                        >
                          <div className="mb-3">
                            <h6 className="mb-1 text-sm font-semibold text-slate-800">
                              {group.name}
                            </h6>
                            <p className="mb-0 text-xs text-slate-500">
                              최소 {group.min_select}개 / 최대{" "}
                              {group.max_select}개
                              {group.is_required ? " · 필수" : " · 선택"}
                            </p>
                          </div>
                          <div className="space-y-2">
                            {(group.options || []).map((option) => {
                              const checked = selectedIds.includes(option.id);
                              const singleSelect = group.max_select <= 1;
                              return (
                                <label
                                  key={option.id}
                                  className="flex items-center justify-between gap-3 rounded-2xl border border-slate-100 px-3 py-2"
                                >
                                  <div>
                                    <div className="text-sm font-medium text-slate-700">
                                      {option.name}
                                    </div>
                                    <div className="text-xs text-slate-500">
                                      +{formatCurrency(option.extra_price)}
                                    </div>
                                  </div>
                                  <Input
                                    type={singleSelect ? "radio" : "checkbox"}
                                    checked={checked}
                                    onChange={(
                                      event: React.ChangeEvent<HTMLInputElement>,
                                    ) =>
                                      togglePickerOption(
                                        group,
                                        option,
                                        event.target.checked,
                                      )
                                    }
                                    name={`menu-option-group-${group.id}`}
                                  />
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </ModalBody>
              <ModalFooter>
                <Button color="light" onClick={closeMenuPicker}>
                  취소
                </Button>
                <Button color="primary" onClick={handleAddMenuToCart}>
                  장바구니 추가
                </Button>
              </ModalFooter>
            </>
          ) : null}
        </Modal>

        <PostCancelDecisionModal
          isOpen={isPostCancelDecisionOpen}
          order={submittedOrder}
          latestCancelPayment={latestSuccessfulCancel}
          canCancelOrder={canCancelOrderAfterPaymentCancel}
          isSubmitting={Boolean(postCancelActionPending)}
          onReopenPayment={handleReopenPayment}
          onCancelOrder={handleCancelOrderRecord}
        />
      </Container>
    </div>
  );
};

export default CashierPOS;
